// @ts-nocheck - Uses import.meta.env which requires ESNext module
import { useEffect, useRef, useState, useCallback } from 'react'
import posthog from 'posthog-js'
import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'

const BUGHERD_SCRIPT_ID = 'bugherd-sidebar-script'

// Get BugHerd API key from environment variable (set by pnpm onboard from Secret Manager)
const getBugHerdApiKey = () => {
  // Vite environment variable (used in web apps)
  if (import.meta.env?.VITE_BUGHERD_API_KEY) {
    return import.meta.env.VITE_BUGHERD_API_KEY
  }
  // React environment variable fallback
  if (typeof process !== 'undefined' && process.env?.REACT_APP_BUGHERD_API_KEY) {
    return process.env.REACT_APP_BUGHERD_API_KEY
  }
  return ''
}

const getBugHerdScriptUrl = () => {
  const apiKey = getBugHerdApiKey()
  if (!apiKey) return ''
  return `https://www.bugherd.com/sidebarv2.js?apikey=${apiKey}`
}

/**
 * Hook to conditionally load BugHerd sidebar based on PostHog feature flag.
 *
 * Usage:
 * 1. Create the feature flag `bugherd-enabled` in PostHog
 * 2. Target specific users by email, role, or any user property
 * 3. Call useBugHerd() in your app component
 *
 * The hook will:
 * - Load BugHerd script when flag is enabled for the user
 * - Remove BugHerd script when flag is disabled
 * - Handle cleanup on unmount
 *
 * Note: This hook uses the global posthog instance directly to work in both
 * core (no PostHogProvider) and admin (with PostHogProvider) apps.
 */
export const useBugHerd = () => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const scriptLoadedRef = useRef(false)

  const checkFlag = useCallback(() => {
    // Build-time kill switch — if set to 'false' or '0', disable regardless of PostHog
    const envFlag = import.meta.env?.VITE_TEMP_FEATURE_FLAG_ENABLE_BUG_HERD
    if (envFlag === 'false' || envFlag === '0') {
      setIsEnabled(false)
      setIsLoading(false)
      return
    }

    // Allow local override for testing: localStorage.setItem('bugherd-force-enable', 'true')
    const forceEnable = localStorage.getItem('bugherd-force-enable') === 'true'
    if (forceEnable) {
      setIsEnabled(true)
      setIsLoading(false)
      return
    }

    if (!posthog || typeof posthog.getFeatureFlag !== 'function') {
      setIsEnabled(false)
      setIsLoading(false)
      return
    }

    try {
      const flagValue = posthog.getFeatureFlag(FeatureFlagKeys.BugHerdEnabled)
      setIsEnabled(flagValue === true)
    } catch (error) {
      console.warn('Error checking BugHerd feature flag:', error)
      setIsEnabled(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Listen for feature flag changes
  useEffect(() => {
    checkFlag()

    if (posthog && typeof posthog.onFeatureFlags === 'function') {
      posthog.onFeatureFlags(checkFlag)
    }
  }, [checkFlag])

  useEffect(() => {
    // Wait for feature flag to load
    if (isLoading) return

    const shouldLoad = isEnabled === true
    const scriptUrl = getBugHerdScriptUrl()

    if (shouldLoad && !scriptLoadedRef.current && scriptUrl) {
      // Load BugHerd script
      const existingScript = document.getElementById(BUGHERD_SCRIPT_ID)
      if (!existingScript) {
        const script = document.createElement('script')
        script.id = BUGHERD_SCRIPT_ID
        script.type = 'text/javascript'
        script.src = scriptUrl
        script.async = true
        document.head.appendChild(script)
        scriptLoadedRef.current = true
      }
    } else if (!shouldLoad && scriptLoadedRef.current) {
      // Remove BugHerd script and cleanup
      const script = document.getElementById(BUGHERD_SCRIPT_ID)
      if (script) {
        script.remove()
      }
      // Clean up BugHerd UI elements if they exist
      const bugherdElements = document.querySelectorAll('[id^="bugherd"]')
      bugherdElements.forEach((el) => el.remove())
      scriptLoadedRef.current = false
    }

    return () => {
      // Cleanup on unmount
      if (scriptLoadedRef.current) {
        const script = document.getElementById(BUGHERD_SCRIPT_ID)
        if (script) {
          script.remove()
        }
        const bugherdElements = document.querySelectorAll('[id^="bugherd"]')
        bugherdElements.forEach((el) => el.remove())
        scriptLoadedRef.current = false
      }
    }
  }, [isEnabled, isLoading])

  return { isEnabled: isEnabled === true, isLoading }
}
